import type { MedicaidEnrollmentDto } from '../types';

// individual：solo 或只有一个 provider 的 practice，答整套 business + disclosures（本人即 100% owner）
// rendering：2 个以上 provider 的 practice 里的某个 provider，只答个人那几题（16b + disclosures）
// group：practice 级，在 Set up your practice 里由 owner 答一次（business + owners / delegated / signer）
export type TMedicaidVariant = 'individual' | 'rendering' | 'group';

// 预填依据（10/15，纯前端）：都是 provider 已经答过的东西，后端不做预填
export type TMedicaidDerive = {
    // Verification 的 Disclosures 里 LICENSE_ACTION 答了 No
    licenseActionNo?: boolean;
    // 任一州 Medicaid 已注册
    everEnrolled?: boolean;
    // 已有 payout 账户
    payoutOnFile?: boolean;
};

export const MEDICAID_DERIVED_HINT = {
    disclosure: 'Answered from your Disclosures step. Change it if that is not right.',
    everMedicaid: 'Answered from the state you are already enrolled in. Change it if that is not right.',
    payMethod: 'Set to the payout account you already added. Change it if that is not right.',
};

// 原型 mcdSeedDerived：只填还没答（null）的字段，答过的不覆盖。everMedicaid 只会预填成 true——
// 所有州都是 No 不代表从没参加过。group 版没有 17 / 18 的个人 disclosure，rendering 没有 payment
export const seedMedicaidEnrollment = (
    dto: MedicaidEnrollmentDto | null | undefined,
    derive: TMedicaidDerive,
    variant: TMedicaidVariant,
): MedicaidEnrollmentDto | null => {
    const cur = dto || {};
    const patch: MedicaidEnrollmentDto = {};
    if (derive.licenseActionNo && variant !== 'group') {
        if (cur.licenseSurrendered == null) patch.licenseSurrendered = false;
        if (cur.licenseDisciplined == null) patch.licenseDisciplined = false;
    }
    if (derive.everEnrolled && cur.everMedicaid == null) patch.everMedicaid = true;
    if (derive.payoutOnFile && variant !== 'rendering' && cur.payMethod == null) patch.payMethod = 'DIRECT_DEPOSIT';
    return Object.keys(patch).length ? { ...cur, ...patch } : null;
};

// 原型 mcdDerivedHint：字段有值且预填依据成立时，在题目下方说明答案从哪来
export const medicaidDerivedHint = (
    dto: MedicaidEnrollmentDto | null | undefined,
    derive: TMedicaidDerive,
    key: 'licenseSurrendered' | 'licenseDisciplined' | 'everMedicaid' | 'payMethod',
): string => {
    const v = dto?.[key];
    if (v == null) return '';
    if ((key === 'licenseSurrendered' || key === 'licenseDisciplined') && derive.licenseActionNo)
        return MEDICAID_DERIVED_HINT.disclosure;
    if (key === 'everMedicaid' && derive.everEnrolled) return MEDICAID_DERIVED_HINT.everMedicaid;
    if (key === 'payMethod' && derive.payoutOnFile) return MEDICAID_DERIVED_HINT.payMethod;
    return '';
};

// 原型 mcdEntityHasAgreement：sole proprietor 没有实体，也就没有 operating agreement
export const entityHasAgreement = (entityType?: string | null) =>
    !!entityType && !String(entityType).startsWith('Individual');
